import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { IMaskModule } from 'angular-imask';
import {
    EMPTY,
    map,
    merge,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { AccessConstants } from '../../../../core/constants/access-constants';
import { ExternalLinks } from '../../../../core/constants/external-links';
import { CardTypeEnum } from '../../../../core/enums/commerce/cards-on-file/card-types';
import {
    InvoiceNet30TransitionablePaymentMethods,
    PaymentMethod,
    PaymentMethodLabelMapping,
} from '../../../../core/enums/commerce/payment/payment-method';
import { parseDateToEasternGridFormat } from '../../../../core/functions/date-helpers';
import { nameOf } from '../../../../core/functions/string-helpers';
import { CreditCardSummary } from '../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { GetEligibleCardsForOrderResponse } from '../../../../core/models/commerce/cards-on-file/get-eligible-cards-for-order-response';
import { OrderCreditCardSummary } from '../../../../core/models/commerce/cards-on-file/order-credit-card-summary';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { CreditCardDialogService } from '../../../../core/services/commerce/credit-card-dialog/credit-card-dialog.service';
import { CreditCardSummaryService } from '../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { CreditCardService } from '../../../../core/services/commerce/credit-card/credit-card.service';
import { CurrencyFormattingService } from '../../../../core/services/currency-formatting/currency-formatting.service';
import { PostalCodeService } from '../../../../core/services/postal-code/postal-code.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { CardTypeIconComponent } from '../../../../shared/components/card-type-icon/card-type-icon.component';
import { MultiSelectFilterComponent } from '../../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { OrderSummaryHeaderComponent } from '../shared/components/order-summary-header/order-summary-header.component';
import { CardOnFileValidationMessages } from '../shared/constants/card-on-file-validation-messages';
import { OrderConstants } from '../shared/constants/order-constants';
import { OrderPaymentConstants } from '../shared/constants/order-payment-constants';
import { OrderSaveConfirmationDialogMessages } from '../shared/constants/order-save-confirmation-dialog-messages';
import { OrderStatus } from '../shared/constants/order-status';
import { PaymentValidationMessages } from '../shared/constants/payment-validation-messages';
import { OrderFormService } from '../shared/services/order-form/order-form.service';
import { PaymentDetailsActionService } from '../shared/services/payment-details-action/payment-details-action.service';
import { PaymentService } from '../shared/services/payment/payment.service';
import { PaymentMessageCellRendererParams } from './cell-renderers/payment-message-cell-renderer/constants/payment-message-cell-renderer-params';
import { PaymentMessageCellRendererComponent } from './cell-renderers/payment-message-cell-renderer/payment-message-cell-renderer.component';
import { PaymentOperationsCellRendererParams } from './cell-renderers/payment-operations-cell-renderer/constants/payment-operations-cell-renderer-params';
import { PaymentOperationsCellRendererComponent } from './cell-renderers/payment-operations-cell-renderer/payment-operations-cell-renderer.component';
import { PaymentStatusCellRendererComponent } from './cell-renderers/payment-status-cell-renderer/payment-status-cell-renderer.component';
import { PaymentDetailsConstants } from './constants/payment-details-constants';
import { PaymentTransactionConstants } from './constants/payment-transaction-constants';
import { PaymentTransactionMaskConfig } from './constants/payment-transaction-mask-config';
import { CreatePaymentRequest } from './models/create-payment-request';
import { CreatePaymentResponse } from './models/create-payment-response';
import { PaymentDetailsFormModel } from './models/payment-details-form-model';
import { PaymentDetailsResponse } from './models/payment-details-response';
import { PaymentDetailsSummaryData } from './models/payment-details-summary-data';
import { PaymentDetailsUpdateRequest } from './models/payment-details-update-request';
import { PaymentHistoryGridRowData } from './models/payment-history-grid-row-data';
import { PaymentTransactionFormModel } from './models/payment-transaction-form-model';
import { PaymentDetailsFormService } from './services/payment-details-form.service';
import { PaymentTransactionFormService } from './services/payment-transaction-form.service';

@Component({
    selector: 'app-payment-details',
    templateUrl: './payment-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatExpansionModule,
        OrderSummaryHeaderComponent,
        MatFormFieldModule,
        MatSelectModule,
        MatRadioModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        IMaskModule,
        RegexInputDirective,
        MatCheckboxModule,
        AgGridAngular,
        HoverTooltipComponent,
        CardTypeIconComponent,
    ],
})
export class PaymentDetailsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    /**
     * Subject that emits when the save action is triggered on the payment details form, allowing subscribers to unsubscribe
     * from form value changes to prevent memory leaks after the form is reset with new data from the API.
     */
    private paymentDetailsSaveAction$ = new Subject<void>();

    /**
     * Subject that emits when the save action is triggered on the payment transaction form, allowing subscribers to react
     * to the save event (e.g., performing the save operation).
     */
    private paymentTransactionSaveAction$ = new Subject<void>();

    private readonly dialog = inject(MatDialog);
    private readonly route = inject(ActivatedRoute);
    private readonly alertService = inject(AlertService);
    private readonly paymentService = inject(PaymentService);
    private readonly dropdownService = inject(DropdownService);
    private readonly userStateService = inject(UserStateService);
    private readonly resourcesService = inject(ResourcesService);
    private readonly orderFormService = inject(OrderFormService);
    private readonly postalCodeService = inject(PostalCodeService);
    private readonly creditCardService = inject(CreditCardService);
    private readonly creditCardDialogService = inject(CreditCardDialogService);
    private readonly creditCardSummaryService = inject(
        CreditCardSummaryService,
    );
    private readonly currencyFormattingService = inject(
        CurrencyFormattingService,
    );
    private readonly paymentDetailsFormService = inject(
        PaymentDetailsFormService,
    );
    private readonly paymentDetailsActionService = inject(
        PaymentDetailsActionService,
    );
    private readonly paymentTransactionFormService = inject(
        PaymentTransactionFormService,
    );

    readonly externalLinks = ExternalLinks;
    readonly cardTypeEnum = CardTypeEnum;
    readonly regexPattern = RegExPattern;
    readonly paymentMethod = PaymentMethod;
    readonly orderConstants = OrderConstants;
    readonly orderPaymentConstants = OrderPaymentConstants;
    readonly paymentDetailsConstants = PaymentDetailsConstants;

    /**
     * Form group for the payment details form.
     */
    paymentDetailsForm: FormGroup<FormGroupType<PaymentDetailsFormModel>> =
        this.paymentDetailsFormService.getFormDefinition();

    /**
     * Form group for the payment transaction form within the payment details page.
     */
    paymentTransactionForm: FormGroup<
        FormGroupType<PaymentTransactionFormModel>
    > | null = null;

    orderId: string | null = null;

    /**
     * The initial status of the order when the payment details page is loaded.
     */
    orderStatus: OrderStatus | null = null;

    /**
     * Indicates whether there are unsaved changes on the page.
     */
    hasChanges = false;

    /**
     * Informational summary data for the payment details page.
     */
    paymentDetailsSummaryData: PaymentDetailsSummaryData =
        {} as PaymentDetailsSummaryData;

    /**
     * The user associated with the order's saved credit cards, used to populate the cards on file dropdown and pre-select the default card if applicable.
     */
    cardsOnFile: OrderCreditCardSummary[] = [];

    paymentHistoryGridContext: unknown;
    paymentHistoryGridRowData: PaymentHistoryGridRowData[] = [];
    paymentHistoryGridApi: GridApi | undefined;
    paymentHistoryGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        domLayout: 'autoHeight',
    };

    permittedOrderStatusTransitions: Partial<
        Record<keyof typeof OrderStatus, OrderStatus[]>
    > = {};

    orderStatusDropdownOptions: DropdownOption[] = [];
    cardsOnFileDropdownOptions: DropdownOption[] = [];

    paymentTransactionAmountMaskConfig = PaymentTransactionMaskConfig.amount;

    get cityStateZip(): string {
        const { city, state, zipCode } = this.paymentDetailsSummaryData;
        const postalCodeFormatted =
            this.postalCodeService.buildPostalCodeString(zipCode ?? '');

        return [city, state, postalCodeFormatted].filter(Boolean).join(', ');
    }

    get status(): FormControl {
        return this.paymentDetailsForm.controls.status as FormControl;
    }

    get poNumber(): FormControl {
        return this.paymentDetailsForm.controls.poNumber as FormControl;
    }

    get selectedCardOnFile(): CreditCardSummary | null {
        return (
            this.cardsOnFile.find(
                (card) =>
                    card.id ===
                    this.paymentTransactionForm?.controls.cardOnFileId.value,
            ) ?? null
        );
    }

    get isBillingInformationComplete(): boolean {
        return this.paymentDetailsSummaryData.isBillingInformationComplete;
    }

    ngOnInit(): void {
        this.loadPaymentDetails().subscribe(() => {
            this.setupOrderStatusDropdownOptions();
            this.setupPaymentDetailsFormListeners();
            this.setupPaymentTransactionFormListeners();
            this.handlePaymentDetailsSaveChangesClick();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.paymentDetailsSaveAction$.next();
        this.paymentDetailsSaveAction$.complete();

        this.paymentTransactionSaveAction$.next();
        this.paymentTransactionSaveAction$.complete();
    }

    /**
     * Handles the grid ready event for the payment history grid, setting the grid API and initializing the grid options such as row data and column definitions.
     */
    onPaymentHistoryGridReady(
        paymentHistoryGridParams: GridReadyEvent<unknown>,
    ) {
        this.paymentHistoryGridApi = paymentHistoryGridParams.api;

        this.paymentHistoryGridApi.setGridOption(
            'rowData',
            this.paymentHistoryGridRowData,
        );
        this.paymentHistoryGridApi.setGridOption(
            'columnDefs',
            this.paymentHistoryGridColumnDefs,
        );
    }

    hasElevatedRole(): boolean {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return AccessConstants.RolesThatCanManagePayments.includes(
            authenticatedUser.role,
        );
    }

    /**
     * Determines whether the payment details form should be in read-only mode.
     * The form is read-only when the order is not in a pending status.
     *
     * @returns True if the order is not in a pending status, false otherwise.
     */
    private isReadOnly(): boolean {
        const statusControl = this.status;

        if (!statusControl) {
            // If the status control is not initialized, default to read-only.
            return true;
        }

        return statusControl.value !== OrderStatus.Pending;
    }

    isApprovedForInvoiceNet30(): boolean {
        return this.paymentDetailsSummaryData.isCreditApproved;
    }

    hasRemainingOutstandingBalance(): boolean {
        return (this.paymentDetailsSummaryData.balance ?? 0) > 0;
    }

    getFormattedOutstandingBalance(): string {
        return this.currencyFormattingService.formatToUSD(
            this.paymentDetailsSummaryData.balance ?? 0,
        );
    }

    isAuthNetAim(paymentMethod: PaymentMethod | null): boolean {
        return paymentMethod === PaymentMethod.AuthNetAim;
    }

    isInvoiceNet30(paymentMethod: PaymentMethod | null): boolean {
        return paymentMethod === PaymentMethod.InvoiceNet30;
    }

    getCardSelectionError(): string | undefined {
        const errors =
            this.paymentTransactionForm?.controls.cardOnFileId.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CardOnFileValidationMessages>('creditCardExpired')
        ] as string | undefined;
    }

    getAmountMaxError(): string | undefined {
        const errors = this.paymentTransactionForm?.controls.amount.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof PaymentValidationMessages>('amountExceedsBalance')
        ] as string | undefined;
    }

    /**
     * Opens the dialog to edit the selected credit card on file, allowing the user to update the card details
     * or change the default card selection. After the dialog is closed, it reloads the credit card data to reflect any changes made.
     */
    editSelectedCreditCard(): void {
        const cardOnFileId =
            this.paymentTransactionForm?.controls.cardOnFileId.value;

        if (!cardOnFileId) {
            throw new Error(
                'Card on file ID is not set. Cannot edit a credit card without a card on file ID.',
            );
        }

        this.creditCardDialogService
            .openEditCardDialog(
                cardOnFileId,
                this.paymentDetailsSummaryData.userId,
                this.cardsOnFile,
            )
            .pipe(
                takeUntil(this.destroy$),
                switchMap((result: boolean) => {
                    if (result) {
                        // If the card was successfully updated, then we want to reload the credit cards
                        // to get the updated list (e.g., if the default card was changed, we want that reflected in the dropdown options).
                        return this.loadCardsOnFile().pipe(
                            tap(() => this.setupCardsOnFileDropdownOptions()),
                        );
                    }

                    return EMPTY;
                }),
            )
            .subscribe();
    }

    /**
     * Opens the dialog to be able to add a new credit card to the order's associated user account, allowing the user to
     * select the new card as a payment method for the order after it's added.
     */
    addNewCreditCard(): void {
        const userId = this.paymentDetailsSummaryData.userId;

        if (!userId) {
            throw new Error(
                'User ID is not set. Cannot add a new credit card without a user ID.',
            );
        }

        if (!this.orderId) {
            throw new Error(
                'Order ID is not set. Cannot add a new credit card without an order ID.',
            );
        }

        this.creditCardDialogService
            .openCreateCardDialog(
                userId,
                Number(this.orderId),
                this.cardsOnFile,
            )
            .pipe(
                takeUntil(this.destroy$),
                switchMap((creditCardSummary: CreditCardSummary | null) => {
                    if (creditCardSummary != null) {
                        return this.getPaymentDetails().pipe(
                            switchMap((response: PaymentDetailsResponse) =>
                                this.loadPaymentTransactionFormData(
                                    response,
                                    creditCardSummary.id,
                                ),
                            ),
                        );
                    }

                    return EMPTY;
                }),
            )
            .subscribe();
    }

    /**
     * Clears the payment details form by resetting it to a new instance of the form definition and unsubscribing from any existing
     * form listeners to prevent memory leaks. This is typically called after successfully saving payment details to reset the form
     * for further edits with the latest data from the API.
     */
    clearPaymentDetailsForm(): void {
        // This is crucial in ensuring that the form listeners unsubscribe before the form is reset
        // to prevent memory leaks and unintended side effects from lingering subscriptions
        // after the form data is refreshed with the latest data from the API.
        this.paymentDetailsSaveAction$.next();

        // Getting a new form instance to reset the form state entirely, including resetting the form's
        // validation state and ensuring that any disabled controls are re-evaluated based on the new form definition.
        this.paymentDetailsForm =
            this.paymentDetailsFormService.getFormDefinition();
    }

    /**
     * Clears the payment transaction form by resetting it to a new instance of the form definition and unsubscribing from any existing
     * form listeners to prevent memory leaks. This is typically called after successfully saving a payment transaction to reset the form
     * for a new entry.
     */
    clearPaymentTransactionForm(): void {
        // This is crucial in ensuring that the form listeners unsubscribe before the form is reset
        // to prevent memory leaks and unintended side effects from lingering subscriptions
        // after the form data is refreshed with the latest data from the API.
        this.paymentTransactionSaveAction$.next();

        // Getting a new form instance to reset the form state entirely.
        this.paymentTransactionForm =
            this.paymentTransactionFormService.getFormDefinition(
                this.paymentDetailsSummaryData.balance,
            );

        // Refreshes and applies the appropriate validators for the payment method after resetting the form
        this.paymentTransactionFormService.refreshAndApplyPaymentMethodValidators(
            this.paymentTransactionForm,
            () => this.cardsOnFile,
        );
    }

    /**
     * Resets the payment transaction form to its initial state by clearing the form values and resetting the
     * form instance, as well as re-setting up the form listeners to ensure they are properly subscribed to
     * the new form instance after the reset.
     */
    resetPaymentTransactionForm(): void {
        this.clearPaymentTransactionForm();

        // After clearing the payment transaction form, we need to re-setup the form listeners to ensure that they are subscribed to the
        // new form instance.
        this.setupPaymentTransactionFormListeners();
    }

    /**
     * Triggers the save action for the payment transaction form, allowing subscribers to react to the save event (e.g., performing the save operation).
     */
    applyPaymentTransaction(): void {
        if (this.paymentTransactionForm?.valid) {
            this.savePaymentTransactionFormDataToApi().subscribe();
        }
    }

    /**
     * Loads the payment details based on the order ID from the route parameters.
     * It fetches the payment details from the API, transforms the response data,
     * and binds the data to the form and page display.
     *
     * @returns An observable that completes when the payment details are loaded and bound.
     */
    private loadPaymentDetails(): Observable<void> {
        return this.route.paramMap.pipe(
            takeUntil(this.destroy$),
            switchMap((params: ParamMap) => {
                this.orderId = params.get('id');

                if (!this.orderId) {
                    throw new Error('Order ID is not set');
                }

                return this.getPaymentDetails();
            }),
            tap((response: PaymentDetailsResponse) => {
                this.loadPaymentDetailsSummaryData(response);
            }),
            tap((response: PaymentDetailsResponse) => {
                // Keep order-level state in sync for operations and status transition logic.
                this.orderStatus = response.status;
            }),
            tap(() => {
                this.refreshPaymentHistoryGridRenderers();
            }),
            tap((response: PaymentDetailsResponse) => {
                this.loadPaymentDetailsFormData(response);
            }),
            tap((response: PaymentDetailsResponse) => {
                this.loadPaymentHistoryGridData(response);
            }),
            switchMap((response: PaymentDetailsResponse) =>
                this.loadPaymentTransactionFormData(response),
            ),
        );
    }

    /**
     * Fetches the payment details for the current order from the API.
     *
     * @returns An observable that emits the payment details response.
     */
    private getPaymentDetails(): Observable<PaymentDetailsResponse> {
        if (!this.orderId) {
            throw new Error('Order ID is not set');
        }

        return this.paymentService.get(this.orderId);
    }

    /**
     * Loads the payment details summary data used for page display.
     *
     * @param response The payment details response from the API.
     */
    private loadPaymentDetailsSummaryData(
        response: PaymentDetailsResponse,
    ): void {
        const { billingInformation, ...order } = response;

        this.paymentDetailsSummaryData = {
            id: order.id,
            userId: order.userId,
            date: order.billingDate
                ? parseDateToEasternGridFormat(order.billingDate)
                : parseDateToEasternGridFormat(order.createdAt),
            customerEmail: order.customerEmail,
            coordinatorName: order.coordinatorName,
            coordinatorEmail: order.coordinatorEmail,
            applicantName: order.licenseeEntityName,
            transmissionLocation: [
                order.transmissionLocationCity,
                order.transmissionLocationState,
            ]
                .filter(Boolean)
                .join(', '),
            salmonLogId: order.salmonLogId,
            pscaId: order.coordinationId,
            callSign: order.callSign || 'New, tba',
            organizationName: billingInformation.organizationName,
            contactName: billingInformation.contactName,
            contactEmail: billingInformation.contactEmail,
            primaryStreetAddress: billingInformation.primaryStreetAddress,
            secondaryStreetAddress: billingInformation.secondaryStreetAddress,
            city: billingInformation.city,
            state: billingInformation.state,
            zipCode: billingInformation.zipCode,
            billingNotes: order.billingNotes,
            orderTotal: this.currencyFormattingService.formatToUSD(
                order.totalPrice,
            ),
            balance: order.balance,
            isCreditApproved: order.isCreditApproved,
            invoicedAt: order.invoicedAt,
            isBillingInformationComplete: order.isBillingInformationComplete,
            poNumber: order.poNumber,
        };
    }

    /**
     * Refreshes existing payment history grid cell renderers to ensure they receive latest component state.
     */
    private refreshPaymentHistoryGridRenderers(): void {
        // Ensure existing cell renderers re-read dynamic params after async data updates
        this.paymentHistoryGridApi?.refreshCells({ force: true });
        this.paymentHistoryGridApi?.redrawRows();
    }

    /**
     * Loads the payment details data into the form for editing.
     *
     * @param response The payment details response from the API.
     */
    private loadPaymentDetailsFormData(response: PaymentDetailsResponse): void {
        this.paymentDetailsForm.patchValue(response);

        // Ensuring access control is updated after binding the form
        this.updatePaymentDetailsFormPermissions();
    }

    /**
     * Enables or disables the payment transaction form based on the order status.
     *
     * @param response The order details response from the API.
     */
    private togglePaymentTransactionForm(status: OrderStatus): void {
        if (status !== OrderStatus.Pending) {
            this.paymentTransactionForm?.disable({ emitEvent: false });
        } else {
            this.paymentTransactionForm?.enable({ emitEvent: false });
        }
    }

    /**
     * Creates a fresh payment transaction form instance for the provided balance.
     */
    private initializePaymentTransactionForm(balance: number): void {
        this.paymentTransactionForm =
            this.paymentTransactionFormService.getFormDefinition(balance);
    }

    /**
     * Loads the payment transaction form data, specifically the user's saved credit cards for the card on file payment method, and
     * sets up the form validators accordingly. If there is a pre-selected card on file (e.g., the default card), it pre-selects that
     * card in the form; otherwise, it applies the preferred/default card selection logic to select the appropriate card on file for
     * the user.
     *
     * @param response The payment details response from the API, used to determine the outstanding balance for setting the amount field and for determining the appropriate validators based on the available cards on file.
     * @param preSelectedCardOnFileId Optional ID of the card on file to pre-select in the form. If not provided, the method will apply the preferred/default card selection logic.
     * @returns An observable that completes when the credit card data is loaded and the form is updated.
     */
    private loadPaymentTransactionFormData(
        response: PaymentDetailsResponse,
        preSelectedCardOnFileId: number | null = null,
    ): Observable<void> {
        return this.loadCardsOnFile().pipe(
            tap(() => {
                this.initializePaymentTransactionForm(response.balance);
                this.togglePaymentTransactionForm(response.status);
            }),
            tap(() => {
                // Pre-filling the amount field in the payment transaction form with the outstanding balance for the order to streamline
                // the process for the user, as they will typically want to pay the full outstanding balance.
                this.paymentTransactionForm?.patchValue({
                    amount: response.balance,
                });
            }),
            tap(() => {
                if (this.paymentTransactionForm) {
                    this.paymentTransactionFormService.selectPreferredOrDefaultCard(
                        this.paymentTransactionForm,
                        () => this.cardsOnFile,
                        preSelectedCardOnFileId,
                    );
                }
            }),
            tap(() => {
                this.setupCardsOnFileDropdownOptions();
            }),
            tap(() => {
                if (this.paymentTransactionForm) {
                    this.paymentTransactionFormService.refreshAndApplyPaymentMethodValidators(
                        this.paymentTransactionForm,
                        () => this.cardsOnFile,
                    );
                }
            }),
            map(() => void 0),
        );
    }

    private loadPaymentHistoryGridData(response: PaymentDetailsResponse): void {
        this.paymentHistoryGridRowData = response.transactions.map(
            (transaction) => ({
                id: transaction.id,
                paymentMethod: transaction.paymentMethod,
                status: transaction.status,
                amount: transaction.amount,
                checkNumber: transaction.checkNumber,
                isInvoiceNet30: transaction.isInvoiceNet30,
                voidedAt: transaction.voidedAt,
                confirmedAt: transaction.confirmedAt,
                createdAt: transaction.createdAt,
            }),
        );
    }

    private loadCardsOnFile(): Observable<void> {
        const orderId = Number(this.orderId);
        if (!orderId) {
            throw new Error('Order ID is not set');
        }

        return this.creditCardService
            .getAllForOrder(this.paymentDetailsSummaryData.userId, orderId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: GetEligibleCardsForOrderResponse) => {
                    // Storing the user's saved credit cards to be used for populating the cards on file
                    // dropdown and for form validation (e.g., checking if the selected card is expired).
                    this.cardsOnFile = response.creditCards;
                }),
                map((): void => undefined),
            );
    }

    private setupPaymentDetailsFormListeners(): void {
        this.paymentDetailsForm.valueChanges
            .pipe(
                takeUntil(merge(this.destroy$, this.paymentDetailsSaveAction$)),
                tap(() => {
                    this.hasChanges = true;
                }),
            )
            .subscribe();

        /**
         * Form control listener for the order status field to update
         * form permissions based on the new status value.
         */
        this.status.valueChanges
            .pipe(
                takeUntil(
                    // Automatically unsubscribe when the component is destroyed or when
                    // the save operation is confirmed
                    merge(this.destroy$, this.paymentDetailsSaveAction$),
                ),
                tap(() => {
                    this.updatePaymentDetailsFormPermissions();
                }),
            )
            .subscribe();
    }

    /**
     * Enables or disables the payment details form based on whether the order is in a read-only state.
     * The form is disabled when the order is not in a pending status, preventing further edits.
     */
    private updatePaymentDetailsFormPermissions(): void {
        if (this.isReadOnly()) {
            this.paymentDetailsForm.disable({ emitEvent: false });

            // The status control should always be available for editing, even in read-only mode.
            this.paymentDetailsForm.controls.status.enable({
                emitEvent: false,
            });
        } else {
            this.paymentDetailsForm.enable({ emitEvent: false });
        }
    }

    private setupPaymentTransactionFormListeners(): void {
        if (!this.paymentTransactionForm) {
            return;
        }

        this.paymentTransactionFormService.setupFormListeners(
            this.paymentTransactionForm,
            () => this.cardsOnFile,
            merge(this.destroy$, this.paymentTransactionSaveAction$),
        );
    }

    private setupOrderStatusDropdownOptions(): void {
        this.resourcesService
            .getPermittedOrderStatusTransitions()
            .pipe(
                takeUntil(this.destroy$),
                tap((permittedOrderStatusTransitions) => {
                    this.permittedOrderStatusTransitions =
                        permittedOrderStatusTransitions;

                    this.orderStatusDropdownOptions =
                        this.orderFormService.getStatusTransitionDropdownOptions(
                            this.permittedOrderStatusTransitions,
                            this.status.value,
                        );
                }),
            )
            .subscribe();
    }

    private setupCardsOnFileDropdownOptions(): void {
        const orderedCreditCards =
            this.creditCardSummaryService.orderCreditCards(this.cardsOnFile);

        this.cardsOnFileDropdownOptions =
            this.dropdownService.transformEligibleCardsForOrderToDropdownOptions(
                orderedCreditCards,
                this.creditCardSummaryService.isExpired,
            );
    }

    /**
     * Handles the click event for saving changes to the payment details.
     * If there are unsaved changes to fields other than status and the order is no longer
     * in a pending status, the user is prompted to confirm before proceeding with the save.
     */
    private handlePaymentDetailsSaveChangesClick(): void {
        this.paymentDetailsActionService.saveChanges$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.saveWithOptionalConfirmation()),
                tap((saveConfirmation) => {
                    if (saveConfirmation) {
                        this.alertService.displaySuccessAlert(
                            this.paymentDetailsConstants.SUCCESS_ALERT_MESSAGE,
                            false,
                            { durationInMs: 5000 },
                        );
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Handles the confirmation or direct save process for the payment details.
     * Confirmation is required when the PO Number has been edited and the order is no longer
     * in a pending status, since those changes would otherwise be discarded by the API.
     * A status-only change does not require confirmation.
     *
     * @returns An observable that emits true if the save was performed, or false if the user cancelled.
     */
    private saveWithOptionalConfirmation(): Observable<boolean> {
        const hasChangesToFormExcludingStatus = this.poNumber.dirty;
        const requiresConfirmation =
            hasChangesToFormExcludingStatus && this.isReadOnly();

        const confirmed$ = requiresConfirmation
            ? this.dialog
                  .open(ModalComponent, {
                      disableClose: true,
                      data: {
                          title: OrderSaveConfirmationDialogMessages.modalTitle,
                          content:
                              OrderSaveConfirmationDialogMessages.modalContent,
                          primaryAction: {
                              title: OrderSaveConfirmationDialogMessages.modalPrimaryActionTitle,
                          },
                      },
                  })
                  .afterClosed()
            : of(true);

        return confirmed$.pipe(
            switchMap((confirmed: boolean) =>
                confirmed
                    ? this.savePaymentDetailsFormDataToApi().pipe(
                          map(() => true),
                      )
                    : of(false),
            ),
        );
    }

    /**
     * Saves the payment details to the API and refreshes the data on the page upon success.
     *
     * @returns An observable that completes when the save operation and data refresh are complete.
     */
    private savePaymentDetailsFormDataToApi(): Observable<void> {
        return this.paymentService
            .save(this.orderId!, this.generatePaymentDetailsRequestObject())
            .pipe(
                switchMap(() => this.getPaymentDetails()),
                tap(() => {
                    this.clearPaymentDetailsForm();
                }),
                tap(() => {
                    // After successfully saving the payment details, we reset the hasChanges flag to false to reflect that there are no longer unsaved changes on the page.
                    this.hasChanges = false;
                }),
                tap((response: PaymentDetailsResponse) => {
                    this.loadPaymentDetailsSummaryData(response);
                }),
                tap((response: PaymentDetailsResponse) => {
                    // Keep order-level state in sync for operations and status transition logic.
                    this.orderStatus = response.status;
                }),
                tap(() => {
                    this.refreshPaymentHistoryGridRenderers();
                }),
                tap((response: PaymentDetailsResponse) => {
                    this.loadPaymentDetailsFormData(response);
                }),
                tap((response: PaymentDetailsResponse) => {
                    this.togglePaymentTransactionForm(response.status);
                }),
                tap(() => {
                    this.setupPaymentDetailsFormListeners();
                }),
                tap(() => {
                    this.orderStatusDropdownOptions =
                        this.orderFormService.getStatusTransitionDropdownOptions(
                            this.permittedOrderStatusTransitions,
                            this.status.value,
                        );
                }),
                map((): void => undefined),
            ) as Observable<void>;
    }

    /**
     * Saves the payment transaction to the API and refreshes the data on the page upon success, including reloading the payment details to reflect any changes resulting from the applied transaction (e.g., updated balance, updated order status, etc.).
     * This also includes re-setting of the payment details and payment transaction forms to reset the form state and validation, as well as re-subscribing to the form listeners to ensure they are properly
     * subscribed to the new form instances after the forms are reset with the latest data from the API.
     *
     * @returns An observable that completes when the save operation and data refresh are complete.
     */
    private savePaymentTransactionFormDataToApi(): Observable<void> {
        return this.paymentService
            .create(
                this.orderId!,
                this.generatePaymentTransactionRequestObject(),
            )
            .pipe(
                tap((response: CreatePaymentResponse) => {
                    if (response.isSuccess) {
                        this.alertService.displaySuccessAlert(
                            PaymentTransactionConstants.SUCCESS_ALERT_MESSAGE,
                            false,
                            { durationInMs: 5000 },
                        );
                    } else {
                        this.alertService.displayErrorAlert(
                            PaymentTransactionConstants.ERROR_ALERT_MESSAGE,
                            true,
                        );
                    }
                }),
                // Clearing the forms and resetting the flag that tracks changes immediately after the API call to save the payment transaction to ensure that
                // the forms are reset and ready for new input.
                tap(() => {
                    this.clearPaymentDetailsForm();
                    this.hasChanges = false;

                    this.clearPaymentTransactionForm();
                }),
                switchMap(() => this.getPaymentDetails()),
                // Loading the payment details display data and form data again to reflect any changes that might have occurred as a result of
                // applying the payment transaction (e.g., updated balance, updated order status, etc.).
                tap((response: PaymentDetailsResponse) => {
                    this.loadPaymentDetailsSummaryData(response);
                }),
                tap((response: PaymentDetailsResponse) => {
                    // Keep order-level state in sync for operations and status transition logic.
                    this.orderStatus = response.status;
                }),
                tap(() => {
                    this.refreshPaymentHistoryGridRenderers();
                }),
                tap((response: PaymentDetailsResponse) => {
                    this.loadPaymentDetailsFormData(response);
                }),
                tap((response: PaymentDetailsResponse) => {
                    this.loadPaymentHistoryGridData(response);
                }),
                switchMap((response: PaymentDetailsResponse) =>
                    this.loadPaymentTransactionFormData(response),
                ),
                // Re-setting up the form listeners after loading the payment details and payment transaction form data again to ensure that
                // the listeners are properly subscribed to the new form instance and reflect the latest data from the API.
                tap(() => {
                    this.setupPaymentDetailsFormListeners();
                    this.setupPaymentTransactionFormListeners();
                }),
                // After applying a payment transaction, the order status might have changed which could impact the available status transitions, so we
                // update the order status dropdown options accordingly to reflect the current state of the order.
                tap(() => {
                    this.orderStatusDropdownOptions =
                        this.orderFormService.getStatusTransitionDropdownOptions(
                            this.permittedOrderStatusTransitions,
                            this.status.value,
                        );
                }),
                map((): void => undefined),
            ) as Observable<void>;
    }

    /**
     * Generates the request object for saving the payment details form data to the API.
     *
     * @returns The PaymentDetailsUpdateRequest object containing the form data.
     */
    private generatePaymentDetailsRequestObject(): PaymentDetailsUpdateRequest {
        const formValue = this.paymentDetailsForm
            .value as PaymentDetailsFormModel;

        return {
            status: formValue.status,
            poNumber: formValue.poNumber,
        };
    }

    /**
     * Generates the request object for saving the payment transaction form data to the API.
     *
     * @returns The CreatePaymentRequest object containing the form data.
     */
    private generatePaymentTransactionRequestObject(): CreatePaymentRequest {
        const formValue = this.paymentTransactionForm
            ?.value as PaymentTransactionFormModel;

        return {
            paymentMethod: formValue.paymentMethod,
            cardOnFileId: formValue.cardOnFileId,
            amount: formValue.amount,
            sendInvoiceUponSave: formValue.sendInvoiceUponSave,
        };
    }

    private get paymentHistoryGridColumnDefs(): ColDef[] {
        return [
            {
                field: nameOf<PaymentHistoryGridRowData>('status'),
                headerName: 'Status',
                flex: 1,
                minWidth: 120,
                maxWidth: 190,
                cellRenderer: PaymentStatusCellRendererComponent,
                filter: false,
                sortable: false,
            },
            {
                field: nameOf<PaymentHistoryGridRowData>('createdAt'),
                headerName: 'Date',
                ...GridHelper.defaultDateColDef,
                flex: 1,
                minWidth: 160,
                maxWidth: 240,
                filter: false,
                sort: 'asc',
            },
            {
                field: nameOf<PaymentHistoryGridRowData>('paymentMethod'),
                headerName: 'Method',
                flex: 1,
                minWidth: 250,
                maxWidth: 350,
                valueGetter: (params) => {
                    const paymentMethod = params.data
                        ?.paymentMethod as PaymentMethod;

                    if (
                        params.data?.isInvoiceNet30 &&
                        InvoiceNet30TransitionablePaymentMethods.includes(
                            paymentMethod,
                        )
                    ) {
                        return `${PaymentMethodLabelMapping[PaymentMethod.InvoiceNet30]} - ${PaymentMethodLabelMapping[paymentMethod]}`;
                    }

                    return (
                        PaymentMethodLabelMapping[paymentMethod] ||
                        params.data?.paymentMethod
                    );
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: Object.values(PaymentMethodLabelMapping)
                        .sort()
                        .map((label) => ({
                            label,
                            value: label,
                            selected: false,
                        })),
                },
            },
            {
                headerName: 'Result Message',
                flex: 1,
                minWidth: 400,
                maxWidth: 500,
                filter: false,
                sortable: false,
                cellRenderer: PaymentMessageCellRendererComponent,
                cellRendererParams: {
                    orderId: this.orderId,
                } as PaymentMessageCellRendererParams,
            },
            {
                field: nameOf<PaymentHistoryGridRowData>('amount'),
                headerName: 'Amount',
                valueFormatter: (params) =>
                    this.currencyFormattingService.formatToUSD(
                        params.value as number,
                    ),
                flex: 1,
                minWidth: 120,
                maxWidth: 190,
                filter: false,
                sortable: false,
            },
            {
                field: nameOf<PaymentHistoryGridRowData>('checkNumber'),
                headerName: 'Check#/Acct',
                flex: 1,
                minWidth: 300,
                filter: false,
                sortable: false,
            },
            {
                headerName: 'Operations',
                flex: 1,
                minWidth: 200,
                maxWidth: 200,
                filter: false,
                sortable: false,
                cellRenderer: PaymentOperationsCellRendererComponent,
                cellRendererParams: () =>
                    ({
                        orderId: this.orderId,
                        orderStatus: this.orderStatus,
                        balance: this.paymentDetailsSummaryData.balance,
                        invoicedAt: this.paymentDetailsSummaryData.invoicedAt,
                        onActionComplete: () => {
                            // After performing an operation on a payment transaction, we want to reload the payment details to reflect any
                            // changes resulting from the operation (e.g., updated balance, updated order status, etc.) and ensure that the
                            // display data and form data are up to date with the latest information from the API.
                            this.getPaymentDetails()
                                .pipe(
                                    takeUntil(this.destroy$),
                                    tap(() => {
                                        this.clearPaymentDetailsForm();

                                        // After performing an operation on a payment transaction, we reset the hasChanges flag to false to reflect that there are no longer unsaved changes on the page.
                                        this.hasChanges = false;
                                    }),
                                    tap((response: PaymentDetailsResponse) => {
                                        this.loadPaymentDetailsSummaryData(
                                            response,
                                        );
                                    }),
                                    tap((response: PaymentDetailsResponse) => {
                                        // Keep order-level state in sync for operations and status transition logic.
                                        this.orderStatus = response.status;
                                    }),
                                    tap(() => {
                                        this.refreshPaymentHistoryGridRenderers();
                                    }),
                                    tap((response: PaymentDetailsResponse) => {
                                        setTimeout(() => {
                                            if (this.paymentTransactionForm) {
                                                this.paymentTransactionFormService.refreshAmountFormControlValueAndValidators(
                                                    this.paymentTransactionForm,
                                                    response.balance,
                                                );
                                            }
                                        });
                                    }),
                                    tap((response: PaymentDetailsResponse) => {
                                        this.loadPaymentHistoryGridData(
                                            response,
                                        );

                                        // Refreshing the payment history grid to ensure that any changes to the transactions resulting from the operation are reflected in the grid display.
                                        this.paymentHistoryGridApi?.setGridOption(
                                            'rowData',
                                            this.paymentHistoryGridRowData,
                                        );
                                    }),
                                    tap((response: PaymentDetailsResponse) => {
                                        this.loadPaymentDetailsFormData(
                                            response,
                                        );
                                    }),
                                    tap(() => {
                                        this.setupPaymentDetailsFormListeners();
                                    }),
                                    tap(() => {
                                        this.orderStatusDropdownOptions =
                                            this.orderFormService.getStatusTransitionDropdownOptions(
                                                this
                                                    .permittedOrderStatusTransitions,
                                                this.status.value,
                                            );
                                    }),
                                )
                                .subscribe();
                        },
                    }) as PaymentOperationsCellRendererParams,
            },
        ];
    }
}
